import {backend, store, navigate} from '@eitje/bridge'

export const buildAuthService = ({
	authChannel,
	scope = 'user',
	tokenState = {
		refreshingToken: false,
		refreshTokenPromise: null,
	},
	logoutRedirectUrl,
	checkAuth = state => !!state.auth.token,
} = {}) => ({
	async login(email, password, {loginRedirectUrl} = {}) {
		const body = {email, password, grant_type: 'password', scope, skipSuccessNotif: true}
		const resp = await backend.post('oauth/token', body)
		if (resp.problem) return resp

		const {data} = resp
		const payload = {
			token: data.access_token,
			expires_at: data.created_at + data.expires_in,
			user: data.user,
			refresh_token: data.refresh_token,
		}

		store.dispatch({type: 'AUTH_SUCCESS', ...payload})
		authChannel?.postMessage({type: 'AUTH_SUCCESS', payload, loginRedirectUrl})
		return payload
	},

	async logout() {
		tokenState.refreshingToken = false
		tokenState.refreshTokenPromise = null

		store.dispatch({type: 'LOGOUT'})
		store.dispatch({type: 'RESET_RECORDS', reset_current_org: true})

		authChannel?.postMessage({type: 'LOGOUT', logoutRedirectUrl})
		authChannel?.postMessage({type: 'RESET_RECORDS', payload: {reset_current_org: true}})

		if (navigate && logoutRedirectUrl) navigate(logoutRedirectUrl)
	},

	async loginFloor({api_key, api_secret, device_info, env_id}) {
		const body = {api_key, api_secret, device_info, env_id, grant_type: 'password', new_vloer: true}
		const resp = await backend.post('oauth/token', body)
		if (!resp.problem) {
			const {access_token, refresh_token, created_at, expires_in, naam: envName} = resp.data
			const payload = {
				type: 'AUTH_SUCCESS',
				token: access_token,
				refresh_token,
				expires_at: created_at + expires_in,
				envId: env_id,
				envName,
			}
			store.dispatch(payload)
		}
		return resp
	},

	async loginFloorUser(pin) {
		const res = await backend.post(`oauth/token?user_specific=true`, {
			scope: 'floor_user',
			grant_type: 'password',
			pin,
		})

		if (res.ok) {
			const {user, access_token} = res.data
			store.dispatch({
				type: 'AUTH_SUCCESS',
				user,
				user_token: access_token,
			})
		}

		return res
	},

	getToken() {
		const state = store.getState()
		return state.auth.token
	},

	checkAuth() {
		const state = store.getState()
		return checkAuth(state)
	},

	async refreshToken() {
		const {auth} = store.getState()
		const startedWithRefreshToken = auth.refresh_token
		const prms = {scope, refresh_token: startedWithRefreshToken, doNotLoad: true, grant_type: 'refresh_token'}
		const res = await backend.post('oauth/token', prms)

		if (!res.problem) {
			const {data} = res
			const currentAuth = store.getState().auth
			const authSessionChanged = currentAuth.refresh_token !== startedWithRefreshToken

			// Ignore refresh responses that belong to an auth session that has since been cleared or replaced.
			if (authSessionChanged) return {access_token: currentAuth.token, expires_at: currentAuth.expires_at}

			const obj = {
				type: 'SET_TOKENS',
				token: data.access_token,
				refresh_token: data.refresh_token,
				expires_at: data.created_at + data.expires_in,
				...(scope === 'public' ? {envId: data.env_id, envName: data.naam} : {}),
			}
			store.dispatch(obj)
			authChannel?.postMessage({type: 'SET_TOKENS', payload: obj})

			return {...data, access_token: data.access_token, expires_at: data.created_at + data.expires_in}
		} else if (res.status === 401) {
			const currentAuth = store.getState().auth
			const authSessionChanged = currentAuth.refresh_token !== startedWithRefreshToken

			// Another refresh or browser tab may have stored newer credentials while this request was in flight.
			if (authSessionChanged) return {access_token: currentAuth.token, expires_at: currentAuth.expires_at}

			this.logout()
		}
		return false
	},

	async refreshTokenIfNeeded() {
		if (tokenState.refreshingToken) return tokenState.refreshTokenPromise

		const cs = Math.round(new Date().getTime() / 1000)
		const state = store.getState()

		if (state.auth.refresh_token && state.auth.expires_at < cs) {
			tokenState.refreshingToken = true
			const _refreshTokenPromise = async () => {
				try {
					const tokenData = await this.refreshToken()
					return tokenData.access_token
				} finally {
					tokenState.refreshingToken = false
				}
			}
			tokenState.refreshTokenPromise = _refreshTokenPromise()
			return tokenState.refreshTokenPromise
		} else {
			return state.auth.token
		}
	},
})
